import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listUsers,
  getUser,
  createUser,
  disableUser,
  enableUser,
  resetUserPassword,
  resetUserMfa,
} from '@/lib/api/users';
import type { ListUsersParams, CreateUserRequest } from '@/types/user';

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params?: ListUsersParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (sub: string) => [...userKeys.details(), sub] as const,
};

export function useUsers(params?: ListUsersParams) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => listUsers(params),
    staleTime: 30_000,
  });
}

export function useUser(sub: string | undefined) {
  return useQuery({
    queryKey: userKeys.detail(sub ?? ''),
    queryFn: () => getUser(sub!),
    enabled: !!sub,
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useDisableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sub: string) => disableUser(sub),
    onSuccess: (_data, sub) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(sub) });
    },
  });
}

export function useEnableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sub: string) => enableUser(sub),
    onSuccess: (_data, sub) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(sub) });
    },
  });
}

export function useResetUserPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sub: string) => resetUserPassword(sub),
    onSuccess: (_data, sub) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(sub) });
    },
  });
}

/**
 * MFA zuruecksetzen (Spec 0045). Nur platform_admin.
 */
export function useResetUserMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sub: string) => resetUserMfa(sub),
    onSuccess: (_data, sub) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(sub) });
    },
  });
}
